import { getEnv } from "@/utils/helpers";

interface WeatherData {
  temperature: number;
  condition: string;
  description: string;
  humidity: number;
  windSpeed: number;
  icon: string;
  feelsLike: number;
  visibility: number;
}

interface WeatherForecast {
  date: string;
  temperature: {
    min: number;
    max: number;
  };
  condition: string;
  icon: string;
  humidity: number;
  windSpeed: number;
}

class WeatherService {
  private apiKey: string;
  private baseUrl = "https://api.openweathermap.org/data/2.5";

  constructor() {
    this.apiKey = getEnv()?.VITE_WEATHER_API_KEY || "";
  }

  async getCurrentWeather(lat: number, lon: number): Promise<WeatherData> {
    const response = await fetch(
      `${this.baseUrl}/weather?lat=${lat}&lon=${lon}&appid=${this.apiKey}&units=metric`
    );

    if (!response.ok) {
      throw new Error("Failed to fetch weather data");
    }

    const data = await response.json();

    return {
      temperature: Math.round(data.main.temp),
      condition: data.weather[0].main,
      description: data.weather[0].description,
      humidity: data.main.humidity,
      windSpeed: data.wind.speed,
      icon: data.weather[0].icon,
      feelsLike: Math.round(data.main.feels_like),
      visibility: data.visibility / 1000, // Convert to km
    };
  }

  async getWeatherForecast(lat: number, lon: number, days = 5): Promise<WeatherForecast[]> {
    const response = await fetch(
      `${this.baseUrl}/forecast?lat=${lat}&lon=${lon}&appid=${this.apiKey}&units=metric`
    );

    if (!response.ok) {
      throw new Error("Failed to fetch weather forecast");
    }

    const data = await response.json();

    // Group by date and get daily forecasts
    const dailyForecasts: { [key: string]: any[] } = {};

    data.list.forEach((item: any) => {
      const date = new Date(item.dt * 1000).toDateString();
      if (!dailyForecasts[date]) {
        dailyForecasts[date] = [];
      }
      dailyForecasts[date].push(item);
    });

    return Object.entries(dailyForecasts)
      .slice(0, days)
      .map(([date, forecasts]) => {
        const temps = forecasts.map(f => f.main.temp);
        const mainForecast = forecasts[0];

        return {
          date,
          temperature: {
            min: Math.round(Math.min(...temps)),
            max: Math.round(Math.max(...temps)),
          },
          condition: mainForecast.weather[0].main,
          icon: mainForecast.weather[0].icon,
          humidity: mainForecast.main.humidity,
          windSpeed: mainForecast.wind.speed,
        };
      });
  }

  async getWeatherByCity(city: string): Promise<WeatherData> {
    const response = await fetch(
      `${this.baseUrl}/weather?q=${city}&appid=${this.apiKey}&units=metric`
    );

    if (!response.ok) {
      throw new Error("Failed to fetch weather data");
    }

    const data = await response.json();

    return {
      temperature: Math.round(data.main.temp),
      condition: data.weather[0].main,
      description: data.weather[0].description,
      humidity: data.main.humidity,
      windSpeed: data.wind.speed,
      icon: data.weather[0].icon,
      feelsLike: Math.round(data.main.feels_like),
      visibility: data.visibility / 1000,
    };
  }

  getWeatherIconUrl(icon: string): string {
    return `https://openweathermap.org/img/wn/${icon}@2x.png`;
  }

  isGoodWeatherForOutdoorEvent(weather: WeatherData): boolean {
    const badConditions = ["Rain", "Thunderstorm", "Snow", "Drizzle"];
    return !badConditions.includes(weather.condition) && weather.windSpeed < 10;
  }
}

export const weatherService = new WeatherService();
